import React, {
  ChangeEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import ModalComponent from "../../../../ui-component/modal";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Input,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { EmployeeService } from "../../../../services/intranet-portal-services";
import { useTheme } from "@mui/material/styles";
import { getProfilePictureUrl } from "../../../../utils/imageUtils";

interface SearchModalProps {
  open: boolean;
  handleClose: () => void;
}

interface UserProfile {
  abpUserId: string;
  userName: string;
  name: string;
  middleName: string;
  surName: string;
  fullName: string;
  designationId: string;
  designationName: string;
  departmentId: string;
  departmentName: string;
  email: string;
  profilePictureUrl: string | null;
  phoneNumber: string;
  hiredDate: string;
}

const SearchModal = ({ open, handleClose }: SearchModalProps): ReactElement => {
  const [isEmpty, setIsEmpty] = useState(true);

  const navigate = useNavigate();

  const theme = useTheme();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);

  const getEmployeeData = useCallback(
    debounce(async (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (value.length < 1) {
        setIsEmpty(true);
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      await EmployeeService.getCoreApiApiAppEmployeeSearchEmployees(value).then((response) => {
        setIsLoading(false);
        setSearchResults(response as UserProfile[]);
      });
      setIsEmpty(false);
    }, 300),
    []
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        margin: 0,
        "& .MuiDialog-container": {
          alignItems: "flex-start"
        },
        "& .MuiPaper-root": {
          mx: 0,
          height: "95vh"
        }
      }}
    >
      <Input
        sx={{
          px: 2,
          py: 1,
          "& input::placeholder": {
            fontSize: "1.3rem"
          }
        }}
        onChange={(e) => {
          void getEmployeeData(e as ChangeEvent<HTMLInputElement>);
        }}
        autoFocus
        placeholder="Search for an employee..."
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon color="error" />
          </InputAdornment>
        }
      />
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto"
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                px: 2
              }}
            >
              <Skeleton width="100%" height={50} />
              <Skeleton width="100%" height={50} />
              <Skeleton width="100%" height={50} />
            </Box>
          ) : searchResults.length > 0 ? (
            <Box>
              <List>
                {searchResults.map((row: any) => (
                  <ListItem
                    key={row.abpUserId}
                    onClick={() => {
                      navigate(`/pages/employee-details?id=${row?.abpUserId as string}`);
                      setSearchResults([]);
                      setIsEmpty(true);
                      handleClose();
                    }}
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.3s ease",
                      ":hover": {
                        background: theme.palette.grey[200]
                      }
                    }}
                  >
                    <Avatar
                      src={getProfilePictureUrl(row?.profilePictureUrl)}
                      sx={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        mr: 3
                      }}
                    />
                    <ListItemText
                      primary={row.fullName}
                      secondary={`${row.designationName as string}, ${
                        row.departmentName as string
                      }`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : isEmpty ? (
            <Box sx={{ mt: 5, textAlign: "center" }}>
              <Typography color="grey">No recent searches</Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 5, textAlign: "center" }}>
              <Typography color="grey">No results found.</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default SearchModal;
